import { useEffect, useRef, useState } from "react";

/**
 * Odometer numbers: tween from the previously shown value to the new one with an
 * ease-out cubic over `ms` milliseconds and return the animated integer. Reduced
 * motion (or a missing rAF) jumps straight to the target, the number is content,
 * the roll is only polish.
 */
export function useAnimatedNumber(value: number, ms = 400): number {
  const [shown, setShown] = useState(value);
  // The value currently on screen, kept in a ref so an interrupted tween
  // restarts from where it visibly is, not from a stale target.
  const shownRef = useRef(value);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const from = shownRef.current;
    if (from === value) return;
    const reduceMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (
      reduceMotion ||
      typeof window === "undefined" ||
      typeof window.requestAnimationFrame !== "function"
    ) {
      shownRef.current = value;
      setShown(value);
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / Math.max(1, ms));
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      const next = Math.round(from + (value - from) * eased);
      shownRef.current = next;
      setShown(next);
      if (t < 1) rafRef.current = window.requestAnimationFrame(tick);
    };
    rafRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [value, ms]);

  return shown;
}
